import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { fetchCitySuggestions, CityData } from '@/services/cityApiService';
import { debounce } from '@/utils/suggestionUtils';

interface CityAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const CityAutocomplete: React.FC<CityAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'Où ? (ville, arrondissement...)',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<CityData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Debounced search function
  const debouncedSearch = useRef(
    debounce(async (query: string) => {
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setIsLoading(true);
      setError(null);

      try {
        abortControllerRef.current = new AbortController();
        const cities = await fetchCitySuggestions(query, 8);
        
        setSuggestions(cities);
        setIsLoading(false);

        // Open dropdown if we have suggestions
        if (cities.length > 0) {
          setOpen(true);
        } else {
          setOpen(false);
        }
      } catch (err) {
        // Ignore abort errors
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        console.error('Error fetching city suggestions:', err);
        setError('Impossible de charger les villes');
        setIsLoading(false);
        setSuggestions([]);
        setOpen(false);
      }
    }, 300)
  ).current;

  // Update suggestions when value changes
  useEffect(() => {
    if (value.length >= 2) {
      debouncedSearch(value);
    } else {
      setSuggestions([]);
      setOpen(false);
      setIsLoading(false);
      setError(null);
    }

    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [value, debouncedSearch]);

  const handleSelect = (cityName: string) => {
    onChange(cityName);
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const hasSuggestions = suggestions.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className={`flex items-center px-4 py-2 bg-gray-50 rounded-lg ${className}`}>
          <MapPin size={20} className="text-gray-400 mr-2 flex-shrink-0" />
          <Input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (hasSuggestions) {
                setOpen(true);
              }
            }}
            className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 p-0"
            autoComplete="off"
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            aria-controls={open ? 'city-suggestions' : undefined}
            aria-label="Rechercher une ville"
          />
          {isLoading && (
            <Loader2 size={16} className="text-gray-400 animate-spin ml-2" />
          )}
        </div>
      </PopoverTrigger>

      {(hasSuggestions || isLoading || error) && (
        <PopoverContent 
          className="w-[var(--radix-popover-trigger-width)] p-0" 
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command>
            <CommandList id="city-suggestions" role="listbox">
              {isLoading ? (
                <div className="py-6 text-center text-sm text-gray-500">
                  <Loader2 className="inline-block animate-spin mr-2" size={16} />
                  Recherche en cours...
                </div>
              ) : error ? (
                <div className="py-6 text-center text-sm text-red-500">
                  {error}
                </div>
              ) : (
                <>
                  {!hasSuggestions && (
                    <CommandEmpty>Aucune ville trouvée</CommandEmpty>
                  )}

                  {hasSuggestions && (
                    <CommandGroup heading="Villes">
                      {suggestions.map((city) => (
                        <CommandItem
                          key={city.code}
                          onSelect={() => handleSelect(city.nom)}
                          className="cursor-pointer min-h-[44px] py-2"
                          role="option"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{city.nom}</span>
                            {city.codesPostaux && city.codesPostaux.length > 0 && (
                              <span className="text-xs text-gray-500">
                                {city.codesPostaux.slice(0, 3).join(', ')}
                                {city.codesPostaux.length > 3 && '...'}
                              </span>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      )}
    </Popover>
  );
};

export default CityAutocomplete;

